import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, ArrowLeft, ArrowRight, BookOpen, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { MarkdownView } from "@/components/MarkdownView";
import { JsonLd } from "@/seo/JsonLd";
import { articleSchema, BUSINESS, breadcrumbSchema } from "@/seo/schemaData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Slugs curated for the landing page teaser — most-requested services in Pachuca.
const FEATURED_SLUGS = [
  "terapia-fisica-vs-quiropractica",
  "hernia-discal-tratamiento-sin-cirugia-pachuca",
  "dolor-lumbar-cronico-pachuca",
  "masaje-deportivo-corredores-ciclistas-pachuca",
  "ozonoterapia-7-padecimientos-pachuca",
  "medicina-funcional-fatiga-cronica-pachuca",
];

const formatDate = (d) =>
  d && new Date(d).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });

// -------- Card --------
const PostCard = ({ p }) => (
  <Link
    to={`/blog/${p.slug}`}
    className="group block text-left rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
    data-testid={`blog-card-${p.slug}`}
  >
    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
      <img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
    </div>
    <div className="p-7">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Calendar className="h-3.5 w-3.5" />
        {p.date && new Date(p.date).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}
        {p.reading_time && (<><span className="text-slate-300">·</span><Clock className="h-3.5 w-3.5" />{p.reading_time}</>)}
      </div>
      <h3 className="mt-3 font-display text-2xl font-bold text-slate-900 leading-tight group-hover:text-green-700 transition-colors">{p.title}</h3>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.excerpt}</p>
      <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
        <span className="font-medium text-slate-700">{p.author}</span>
        <span className="text-slate-300">·</span>
        <span className="font-semibold text-green-700">Leer artículo →</span>
      </div>
    </div>
  </Link>
);

// -------- Landing teaser (6 curated posts) --------
export const Blog = () => {
  const { lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data || [])).catch(() => setPosts([])).finally(() => setLoading(false));
  }, []);

  // Order featured posts in our curated sequence, then fall back to most recent until we have 6
  const featured = (() => {
    const map = new Map(posts.map((p) => [p.slug, p]));
    const out = FEATURED_SLUGS.map((s) => map.get(s)).filter(Boolean);
    if (out.length < 6) {
      const seen = new Set(out.map((p) => p.slug));
      for (const p of posts) {
        if (out.length >= 6) break;
        if (!seen.has(p.slug)) out.push(p);
      }
    }
    return out.slice(0, 6);
  })();

  return (
    <section id="blog" className="relative py-24 md:py-32 bg-slate-50" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-green-700">
              <BookOpen className="h-3.5 w-3.5" /> Blog
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              {lang === "es" ? "Educación para tu bienestar" : "Education for your wellness"}
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              {lang === "es"
                ? "Artículos extensos y honestos sobre fisioterapia, quiropráctica, medicina funcional, ozonoterapia y estética integrativa. Escritos por nuestro equipo clínico."
                : "In-depth honest articles on physiotherapy, chiropractic, functional medicine, ozone therapy and integrative aesthetics."}
            </p>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 self-start md:self-end rounded-full bg-white border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-green-400 hover:text-green-700 transition-colors"
            data-testid="blog-see-all"
          >
            Ver todos los artículos <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {loading && <p className="mt-12 text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>}

        {!loading && featured.length === 0 && (
          <p className="mt-12 text-slate-400 text-sm">No hay artículos publicados aún.</p>
        )}

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((p) => <PostCard key={p.id} p={p} />)}
        </div>

        {!loading && posts.length > featured.length && (
          <div className="mt-12 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 rounded-full bg-green-600 text-white px-6 py-3 text-sm font-semibold hover:bg-green-700 transition-colors"
              data-testid="blog-see-all-bottom"
            >
              Ver los {posts.length} artículos <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

// -------- Full index page at /blog --------
export const BlogIndex = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog · TERAFIQUIRO Wellness Center · Pachuca";
    axios.get(`${API}/blog`).then((r) => setPosts(r.data || [])).catch(() => setPosts([])).finally(() => setLoading(false));
  }, []);

  const schemas = [
    breadcrumbSchema([
      { name: "Inicio", url: BUSINESS.url + "/" },
      { name: "Blog", url: BUSINESS.url + "/blog" },
    ]),
  ];

  return (
    <section className="min-h-screen bg-slate-50 py-20" data-testid="blog-index">
      <JsonLd id="blog-index" data={schemas} />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-green-700 hover:text-green-800 mb-8" data-testid="blog-index-back">
          <ArrowLeft className="h-4 w-4" /> Volver al inicio
        </Link>
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-green-700">
            <BookOpen className="h-3.5 w-3.5" /> Blog · {posts.length} artículos
          </span>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl font-bold tracking-tight text-slate-900">Educación para tu bienestar</h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Artículos clínicos honestos sobre fisioterapia, quiropráctica, medicina funcional, ozonoterapia, herbolaria, terapia neural y estética integrativa. Escritos por nuestro equipo de Pachuca.
          </p>
        </div>

        {loading && <p className="mt-12 text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>}

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((p) => <PostCard key={p.id} p={p} />)}
        </div>
      </div>
    </section>
  );
};

// -------- Article detail at /blog/:slug --------
export const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    axios.get(`${API}/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} · TERAFIQUIRO`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <section className="min-h-screen bg-white py-20 grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </section>
    );
  }

  if (notFound || !post) {
    return (
      <section className="min-h-screen bg-white py-20 grid place-items-center">
        <div className="text-center">
          <p className="text-slate-600">Artículo no encontrado.</p>
          <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-green-700">Volver al blog</Link>
        </div>
      </section>
    );
  }

  const pageUrl = `${BUSINESS.url}/blog/${post.slug}`;
  const schemas = [
    articleSchema(post, pageUrl),
    breadcrumbSchema([
      { name: "Inicio", url: BUSINESS.url + "/" },
      { name: "Blog", url: BUSINESS.url + "/blog" },
      { name: post.title, url: pageUrl },
    ]),
  ];

  return (
    <section className="min-h-screen bg-white py-20" data-testid={`article-${post.slug}`}>
      <JsonLd id={`article-${post.slug}`} data={schemas} />
      <div className="max-w-3xl mx-auto px-6 lg:px-0">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-green-700 hover:text-green-800 mb-6"
          data-testid="blog-back"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>

        <div className="aspect-[16/9] rounded-3xl overflow-hidden shadow-lg shadow-slate-900/10 mb-8">
          <img src={post.cover} alt={post.title} className="h-full w-full object-cover" />
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {formatDate(post.date)}
          </span>
          {post.reading_time && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {post.reading_time}
            </span>
          )}
          <span className="text-slate-400">·</span>
          <span className="font-medium text-slate-700">{post.author}</span>
        </div>

        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.1]">
          {post.title}
        </h1>

        {post.excerpt && (
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>
        )}

        <div className="mt-8"><MarkdownView text={post.content} withTOC /></div>

        {post.tags?.length > 0 && (
          <ul className="mt-12 flex flex-wrap gap-2 pt-8 border-t border-slate-100">
            {post.tags.map((tag, i) => (
              <li key={i} className="inline-flex rounded-full bg-green-50 border border-green-100 px-3 py-1 text-xs font-medium text-green-700">
                #{tag}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-12 rounded-3xl bg-gradient-to-br from-green-600 to-emerald-700 p-8 md:p-10 text-white text-center">
          <h3 className="font-display text-2xl font-bold">¿Listo para agendar tu valoración?</h3>
          <p className="mt-2 text-green-50/90 text-sm">Reserva en línea o por WhatsApp. Te respondemos en minutos.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/#booking-fisio" className="inline-flex items-center rounded-full bg-white text-green-700 px-6 py-3 text-sm font-semibold hover:bg-green-50">Agendar cita</Link>
            <a href="https://wa.me/527712141634" target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full bg-[#25D366] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1ebe5d]">WhatsApp</a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Blog;
